import { jwApi, isEmptyResponse } from './chunk-WDPTOZON.js';
export { JwApiError, jwApi } from './chunk-WDPTOZON.js';
export { permissions } from './chunk-5S5ELQWN.js';
export { JwAuthError, auth } from './chunk-GAKGNVWY.js';
import { config } from './chunk-WHCMK6SD.js';
export { config } from './chunk-WHCMK6SD.js';
import 'zod';

// src/config/apply.ts
async function applyConfig(env) {
  switch (env) {
    case "dev":
      return await import('./env-dev-WXKRMHY4.js');
    case "stg":
      return await import('./env-stg-BBVQNW36.js');
    case "prd":
      return await import('./env-prd-QJGNTNE2.js');
  }
}

// ../base/fetch/xhr.ts
var AbortError = class extends Error {
  name = "AbortError";
  message = "Aborted";
  xhr;
  evt;
  constructor({ xhr, evt }) {
    super("Aborted");
    this.xhr = xhr;
    this.evt = evt;
  }
};
var XhrError = class extends Error {
  name = "XhrError";
  message = "XHR Error";
  xhr;
  data;
  evt;
  headers;
  constructor({
    xhr,
    data,
    evt,
    headers
  }) {
    super("XHR Error");
    this.xhr = xhr;
    this.data = data;
    this.evt = evt;
    this.headers = headers;
  }
};
var parseHeaders = (request) => {
  const headers = request.getAllResponseHeaders();
  const arr = headers.trim().split(/[\r\n]+/);
  return arr.reduce((acc, line) => {
    const parts = line.split(": ");
    const header = parts.shift()?.trim();
    const value = parts.join(": ");
    acc[header] = value;
    return acc;
  }, {});
};
async function httpPut(url, blob, opts) {
  const fileArr = new Uint8Array(await blob.arrayBuffer());
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.overrideMimeType("application/json");
    Object.entries(opts?.headers ?? {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    if (opts?.authToken) {
      xhr.setRequestHeader("Authorization", opts.authToken);
    }
    if (opts?.onProgress) {
      xhr.upload.addEventListener("progress", function(e) {
        const completion = e.loaded / e.total;
        opts.onProgress?.(completion);
      });
    }
    if (opts?.signal) {
      const abort = () => {
        xhr.abort();
      };
      opts.signal.addEventListener("abort", abort);
      xhr.addEventListener("readystatechange", () => {
        if (xhr.readyState === 4) {
          opts.signal?.removeEventListener("abort", abort);
        }
      });
      xhr.addEventListener("abort", (evt) => {
        reject(new AbortError({ xhr, evt }));
      });
    }
    xhr.addEventListener("load", (evt) => {
      let json;
      if (xhr.responseText && !isEmptyResponse(xhr.status, xhr.getResponseHeader("content-length"))) {
        try {
          json = JSON.parse(xhr.responseText);
        } catch (err) {
        }
      }
      const headers = parseHeaders(xhr);
      if (xhr.status >= 200 && xhr.status < 400) {
        resolve({ xhr, json, headers });
      } else {
        reject(new XhrError({ xhr, data: json, evt, headers }));
      }
    });
    xhr.addEventListener("error", (evt) => {
      reject(new XhrError({ xhr, evt }));
    });
    xhr.addEventListener("timeout", (evt) => {
      reject(new XhrError({ xhr, evt }));
    });
    xhr.send(fileArr);
  });
}

// ../base/upload/manager.ts
var DEFAULT_SETTINGS = {
  startNextInQueueOn: ["finished", "aborted", "failed"],
  maxSimultaneousUploads: 1
};
var _uploads = [];
var _settings = structuredClone(DEFAULT_SETTINGS);
var _subscribers = [];
var _fireSubscribers = () => {
  _subscribers.forEach((f) => {
    f([..._uploads]);
  });
};
var UploadManager = class {
  static resetSettings() {
    _settings = structuredClone(DEFAULT_SETTINGS);
  }
  static updateSettings(fn) {
    _settings = fn(_settings);
  }
  static getSettings() {
    return structuredClone(_settings);
  }
  static getAll() {
    return [..._uploads];
  }
  static canStartUpload() {
    const activeUploads = _uploads.filter((s) => s.getState().state === "active");
    if (activeUploads.length >= _settings.maxSimultaneousUploads) {
      return false;
    }
    return true;
  }
  static startNextQueuedUpload(force = false) {
    const queuedUpload = _uploads.find((u) => u.getState().state === "queued");
    if (queuedUpload) {
      queuedUpload.start(force);
    }
  }
  static subscribe(fn) {
    if (!_subscribers.includes(fn)) {
      _subscribers.push(fn);
    }
    fn([..._uploads]);
    return () => {
      _subscribers = _subscribers.filter((f) => f !== fn);
    };
  }
};
var UploadManagerInternal = class extends UploadManager {
  static add(upload) {
    _uploads.push(upload);
    _fireSubscribers();
  }
  static remove(upload) {
    _uploads = _uploads.filter((u) => u !== upload);
    _fireSubscribers();
  }
};

// ../base/upload/base.ts
var initialUploadState = {
  state: "idle",
  progress: 0,
  errors: [],
  asset: void 0
};
var Upload = class {
  /**
   * Whether this upload type can be paused
   */
  canPause = false;
  metadata;
  createAsset;
  /**
   * Upload identifier
   */
  _identifier;
  get identifier() {
    return this._identifier;
  }
  getIdentifier;
  /**
   * Upload state
   */
  state = { ...initialUploadState };
  /**
   * Array of subscriber functions to call on state change
   *
   */
  subscribers = [];
  debug = false;
  _log(...args) {
    if (!this.debug) {
      return;
    }
    console.debug(...args);
  }
  constructor(params) {
    this.metadata = params.metadata;
    this._identifier = params.getIdentifier(null);
    this.getIdentifier = params.getIdentifier;
    this.createAsset = async () => {
      try {
        const asset = await params.createAsset();
        this.setState((s) => {
          s.asset = asset;
        });
        this._identifier = this.getIdentifier(asset.id);
        return asset;
      } catch (err) {
        this.setState((s) => {
          s.errors = [...s.errors, err];
        });
      }
    };
    this.debug = params.debug ?? false;
    UploadManagerInternal.add(this);
    this._log("Created Upload", this);
  }
  async handleUploadFinished() {
    this._log("Upload finished", this);
    this.setState((s) => {
      s.progress = 1;
      s.state = "finished";
    });
    if (UploadManagerInternal.getSettings().startNextInQueueOn.includes("finished")) {
      UploadManagerInternal.startNextQueuedUpload();
    }
  }
  setState(updater) {
    const prevState = { ...this.state };
    const nextState = { ...this.state };
    updater(nextState);
    this.state = { ...this.state, ...nextState };
    this.subscribers.forEach((s) => s(this.state, prevState));
  }
  subscribe(subscriber) {
    this.subscribers.push(subscriber);
    const state = this.getState();
    subscriber(state, state);
    return () => {
      this.subscribers = this.subscribers.filter((s) => s !== subscriber);
    };
  }
  getState() {
    return { ...this.state };
  }
  async start(force = false) {
    if (this.state.state === "finished" || this.state.state === "aborted" || this.state.state === "failed") {
      return;
    }
    if (this.state.state === "active") {
      return;
    }
    if (!UploadManagerInternal.canStartUpload() && !force) {
      this.setState((s) => {
        s.state = "queued";
      });
      return;
    }
    this.setState((s) => {
      s.state = "active";
    });
    if (!this.getState().asset) {
      await this.createAsset();
    }
    return true;
  }
  pause() {
    if (!this.canPause) {
      return;
    }
    const { state } = this.state;
    if (state === "finished" || state === "aborted" || state === "failed") {
      return;
    }
    if (state !== "active") {
      return;
    }
    this.setState((s) => {
      s.state = "paused";
    });
    if (UploadManagerInternal.getSettings().startNextInQueueOn.includes("paused")) {
      UploadManagerInternal.startNextQueuedUpload();
    }
  }
  abort() {
    const { state } = this.state;
    if (state === "finished" || state === "aborted" || state === "failed") {
      return;
    }
    this.setState((s) => {
      s.state = "aborted";
    });
    if (UploadManagerInternal.getSettings().startNextInQueueOn.includes("aborted")) {
      UploadManagerInternal.startNextQueuedUpload();
    }
    return true;
  }
  remove() {
    const { state } = this.state;
    if (!(state === "finished" || state === "aborted" || state === "failed" || state === "idle")) {
      return;
    }
    UploadManagerInternal.remove(this);
  }
  when(requiredState) {
    return new Promise((resolve) => {
      if (this.state.state === requiredState) {
        return resolve();
      }
      const unsub = this.subscribe((nextState) => {
        if (nextState.state === requiredState) {
          unsub();
          return resolve();
        }
      });
    });
  }
};

// ../base/upload/base-file.ts
var UploadFile = class extends Upload {
  /**
   * Source file to upload
   */
  file;
  abortController = new AbortController();
  constructor(params) {
    super(params);
    this.file = params.file;
  }
  async _upload() {
    throw new Error("Not implemented");
  }
  async start(force = false) {
    if (await super.start(force) !== true) {
      return;
    }
    this._upload();
    return true;
  }
  abort() {
    if (super.abort() !== true) {
      return;
    }
    this.abortController.abort();
  }
};

// ../base/upload/methods/direct.ts
var UploadDirect = class extends UploadFile {
  mimeType;
  constructor(params) {
    super(params);
    this.mimeType = params.mimeType;
  }
  getHeaders() {
    const headers = {
      "Content-Disposition": `attachment; filename=${encodeURIComponent(this.file.name)}`
    };
    if (this.mimeType) {
      headers["Content-Type"] = this.mimeType;
    }
    return headers;
  }
  handleError(err) {
    if (this.abortController.signal.aborted) {
      this.setState((s) => {
        s.state = "aborted";
      });
      this.debug && console.debug("Upload aborted", this);
      return;
    }
    this.setState((s) => {
      s.state = "failed";
    });
    this.debug && console.debug("Upload failed", this);
    throw err;
  }
  async _upload() {
    const uploadLink = this.getState().asset?.upload_link;
    if (!uploadLink) {
      throw new Error("Asset is missing upload_link");
    }
    if (this.state.state === "finished" || this.state.state === "failed" || this.state.state === "aborted" || this.state.state === "paused") {
      return;
    }
    try {
      await httpPut(uploadLink, this.file, {
        headers: this.getHeaders(),
        signal: this.abortController.signal,
        onProgress: (progress) => {
          this.debug && console.debug("Upload progress", this, progress);
          this.setState((s) => {
            s.progress = progress * 0.9;
          });
        }
      });
      this.handleUploadFinished();
    } catch (err) {
      this.handleError(err);
    }
  }
};

// ../base/upload/base-url.ts
var UploadUrl = class extends Upload {
  url;
  constructor(params) {
    super(params);
    this.url = params.url;
  }
  async start() {
    if (await super.start() !== true) {
      return;
    }
    await this.handleUploadFinished();
    return true;
  }
};

// ../base/upload/methods/external.ts
var UploadExternal = class extends UploadUrl {
};

// ../base/upload/methods/fetch.ts
var UploadFetch = class extends UploadUrl {
};

// ../../node_modules/hash-wasm/dist/index.esm.js
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function(resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function(resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, [])).next());
  });
}
var Mutex = class {
  constructor() {
    this.mutex = Promise.resolve();
  }
  lock() {
    let begin = () => {
    };
    this.mutex = this.mutex.then(() => new Promise(begin));
    return new Promise((res) => {
      begin = res;
    });
  }
  dispatch(fn) {
    return __awaiter(this, void 0, void 0, function* () {
      const unlock = yield this.lock();
      try {
        return yield Promise.resolve(fn());
      } finally {
        unlock();
      }
    });
  }
};
var _a;
function getGlobal() {
  if (typeof globalThis !== "undefined")
    return globalThis;
  if (typeof self !== "undefined")
    return self;
  if (typeof window !== "undefined")
    return window;
  return global;
}
var globalObject = getGlobal();
var nodeBuffer = (_a = globalObject.Buffer) !== null && _a !== void 0 ? _a : null;
var textEncoder = globalObject.TextEncoder ? new globalObject.TextEncoder() : null;
function hexCharCodesToInt(a, b) {
  return (a & 15) + (a >> 6 | a >> 3 & 8) << 4 | (b & 15) + (b >> 6 | b >> 3 & 8);
}
function writeHexToUInt8(buf, str) {
  const size = str.length >> 1;
  for (let i = 0; i < size; i++) {
    const index = i << 1;
    buf[i] = hexCharCodesToInt(str.charCodeAt(index), str.charCodeAt(index + 1));
  }
}
function hexStringEqualsUInt8(str, buf) {
  if (str.length !== buf.length * 2) {
    return false;
  }
  for (let i = 0; i < buf.length; i++) {
    const strIndex = i << 1;
    if (buf[i] !== hexCharCodesToInt(str.charCodeAt(strIndex), str.charCodeAt(strIndex + 1))) {
      return false;
    }
  }
  return true;
}
var alpha = "a".charCodeAt(0) - 10;
var digit = "0".charCodeAt(0);
function getDigestHex(tmpBuffer, input, hashLength) {
  let p = 0;
  for (let i = 0; i < hashLength; i++) {
    let nibble = input[i] >>> 4;
    tmpBuffer[p++] = nibble > 9 ? nibble + alpha : nibble + digit;
    nibble = input[i] & 15;
    tmpBuffer[p++] = nibble > 9 ? nibble + alpha : nibble + digit;
  }
  return String.fromCharCode.apply(null, tmpBuffer);
}
var getUInt8Buffer = nodeBuffer !== null ? (data) => {
  if (typeof data === "string") {
    const buf = nodeBuffer.from(data, "utf8");
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.length);
  }
  if (nodeBuffer.isBuffer(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.length);
  }
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  throw new Error("Invalid data type!");
} : (data) => {
  if (typeof data === "string") {
    return textEncoder.encode(data);
  }
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }
  throw new Error("Invalid data type!");
};
var base64Chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
var base64Lookup = new Uint8Array(256);
for (let i = 0; i < base64Chars.length; i++) {
  base64Lookup[base64Chars.charCodeAt(i)] = i;
}
function getDecodeBase64Length(data) {
  let bufferLength = Math.floor(data.length * 0.75);
  const len = data.length;
  if (data[len - 1] === "=") {
    bufferLength -= 1;
    if (data[len - 2] === "=") {
      bufferLength -= 1;
    }
  }
  return bufferLength;
}
function decodeBase64(data) {
  const bufferLength = getDecodeBase64Length(data);
  const len = data.length;
  const bytes = new Uint8Array(bufferLength);
  let p = 0;
  for (let i = 0; i < len; i += 4) {
    const encoded1 = base64Lookup[data.charCodeAt(i)];
    const encoded2 = base64Lookup[data.charCodeAt(i + 1)];
    const encoded3 = base64Lookup[data.charCodeAt(i + 2)];
    const encoded4 = base64Lookup[data.charCodeAt(i + 3)];
    bytes[p] = encoded1 << 2 | encoded2 >> 4;
    p += 1;
    bytes[p] = (encoded2 & 15) << 4 | encoded3 >> 2;
    p += 1;
    bytes[p] = (encoded3 & 3) << 6 | encoded4 & 63;
    p += 1;
  }
  return bytes;
}
var MAX_HEAP = 16 * 1024;
var WASM_FUNC_HASH_LENGTH = 4;
var wasmMutex = new Mutex();
var wasmModuleCache = /* @__PURE__ */ new Map();
function WASMInterface(binary, hashLength) {
  return __awaiter(this, void 0, void 0, function* () {
    let wasmInstance = null;
    let memoryView = null;
    let initialized = false;
    if (typeof WebAssembly === "undefined") {
      throw new Error("WebAssembly is not supported in this environment!");
    }
    const writeMemory = (data, offset = 0) => {
      memoryView.set(data, offset);
    };
    const getMemory = () => memoryView;
    const getExports = () => wasmInstance.exports;
    const setMemorySize = (totalSize) => {
      wasmInstance.exports.Hash_SetMemorySize(totalSize);
      const arrayOffset = wasmInstance.exports.Hash_GetBuffer();
      const memoryBuffer = wasmInstance.exports.memory.buffer;
      memoryView = new Uint8Array(memoryBuffer, arrayOffset, totalSize);
    };
    const getStateSize = () => {
      const view = new DataView(wasmInstance.exports.memory.buffer);
      const stateSize = view.getUint32(wasmInstance.exports.STATE_SIZE, true);
      return stateSize;
    };
    const loadWASMPromise = wasmMutex.dispatch(() => __awaiter(this, void 0, void 0, function* () {
      if (!wasmModuleCache.has(binary.name)) {
        const asm = decodeBase64(binary.data);
        const promise = WebAssembly.compile(asm);
        wasmModuleCache.set(binary.name, promise);
      }
      const module = yield wasmModuleCache.get(binary.name);
      wasmInstance = yield WebAssembly.instantiate(module, {
        // env: {
        //   emscripten_memcpy_big: (dest, src, num) => {
        //     const memoryBuffer = wasmInstance.exports.memory.buffer;
        //     const memView = new Uint8Array(memoryBuffer, 0);
        //     memView.set(memView.subarray(src, src + num), dest);
        //   },
        //   print_memory: (offset, len) => {
        //     const memoryBuffer = wasmInstance.exports.memory.buffer;
        //     const memView = new Uint8Array(memoryBuffer, 0);
        //     console.log('print_int32', memView.subarray(offset, offset + len));
        //   },
        // },
      });
    }));
    const setupInterface = () => __awaiter(this, void 0, void 0, function* () {
      if (!wasmInstance) {
        yield loadWASMPromise;
      }
      const arrayOffset = wasmInstance.exports.Hash_GetBuffer();
      const memoryBuffer = wasmInstance.exports.memory.buffer;
      memoryView = new Uint8Array(memoryBuffer, arrayOffset, MAX_HEAP);
    });
    const init = (bits = null) => {
      initialized = true;
      wasmInstance.exports.Hash_Init(bits);
    };
    const updateUInt8Array = (data) => {
      let read = 0;
      while (read < data.length) {
        const chunk = data.subarray(read, read + MAX_HEAP);
        read += chunk.length;
        memoryView.set(chunk);
        wasmInstance.exports.Hash_Update(chunk.length);
      }
    };
    const update = (data) => {
      if (!initialized) {
        throw new Error("update() called before init()");
      }
      const Uint8Buffer = getUInt8Buffer(data);
      updateUInt8Array(Uint8Buffer);
    };
    const digestChars = new Uint8Array(hashLength * 2);
    const digest = (outputType, padding = null) => {
      if (!initialized) {
        throw new Error("digest() called before init()");
      }
      initialized = false;
      wasmInstance.exports.Hash_Final(padding);
      if (outputType === "binary") {
        return memoryView.slice(0, hashLength);
      }
      return getDigestHex(digestChars, memoryView, hashLength);
    };
    const save = () => {
      if (!initialized) {
        throw new Error("save() can only be called after init() and before digest()");
      }
      const stateOffset = wasmInstance.exports.Hash_GetState();
      const stateLength = getStateSize();
      const memoryBuffer = wasmInstance.exports.memory.buffer;
      const internalState = new Uint8Array(memoryBuffer, stateOffset, stateLength);
      const prefixedState = new Uint8Array(WASM_FUNC_HASH_LENGTH + stateLength);
      writeHexToUInt8(prefixedState, binary.hash);
      prefixedState.set(internalState, WASM_FUNC_HASH_LENGTH);
      return prefixedState;
    };
    const load = (state) => {
      if (!(state instanceof Uint8Array)) {
        throw new Error("load() expects an Uint8Array generated by save()");
      }
      const stateOffset = wasmInstance.exports.Hash_GetState();
      const stateLength = getStateSize();
      const overallLength = WASM_FUNC_HASH_LENGTH + stateLength;
      const memoryBuffer = wasmInstance.exports.memory.buffer;
      if (state.length !== overallLength) {
        throw new Error(`Bad state length (expected ${overallLength} bytes, got ${state.length})`);
      }
      if (!hexStringEqualsUInt8(binary.hash, state.subarray(0, WASM_FUNC_HASH_LENGTH))) {
        throw new Error("This state was written by an incompatible hash implementation");
      }
      const internalState = state.subarray(WASM_FUNC_HASH_LENGTH);
      new Uint8Array(memoryBuffer, stateOffset, stateLength).set(internalState);
      initialized = true;
    };
    const isDataShort = (data) => {
      if (typeof data === "string") {
        return data.length < MAX_HEAP / 4;
      }
      return data.byteLength < MAX_HEAP;
    };
    let canSimplify = isDataShort;
    switch (binary.name) {
      case "argon2":
      case "scrypt":
        canSimplify = () => true;
        break;
      case "blake2b":
      case "blake2s":
        canSimplify = (data, initParam) => initParam <= 512 && isDataShort(data);
        break;
      case "blake3":
        canSimplify = (data, initParam) => initParam === 0 && isDataShort(data);
        break;
      case "xxhash64":
      case "xxhash3":
      case "xxhash128":
        canSimplify = () => false;
        break;
    }
    const calculate = (data, initParam = null, digestParam = null) => {
      if (!canSimplify(data, initParam)) {
        init(initParam);
        update(data);
        return digest("hex", digestParam);
      }
      const buffer = getUInt8Buffer(data);
      memoryView.set(buffer);
      wasmInstance.exports.Hash_Calculate(buffer.length, initParam, digestParam);
      return getDigestHex(digestChars, memoryView, hashLength);
    };
    yield setupInterface();
    return {
      getMemory,
      writeMemory,
      getExports,
      setMemorySize,
      init,
      update,
      digest,
      save,
      load,
      calculate,
      hashLength
    };
  });
}
function lockedCreate(mutex2, binary, hashLength) {
  return __awaiter(this, void 0, void 0, function* () {
    const unlock = yield mutex2.lock();
    const wasm = yield WASMInterface(binary, hashLength);
    unlock();
    return wasm;
  });
}
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
var name$d = "md5";
var data$d = "AGFzbQEAAAABEgRgAAF/YAAAYAF/AGACf38BfwMIBwABAgMBAAIFBAEBAgIGDgJ/AUGgigULfwBBgAgLB3AIBm1lbW9yeQIADkhhc2hfR2V0QnVmZmVyAAAJSGFzaF9Jbml0AAELSGFzaF9VcGRhdGUAAgpIYXNoX0ZpbmFsAAQNSGFzaF9HZXRTdGF0ZQAFDkhhc2hfQ2FsY3VsYXRlAAYKU1RBVEVfU0laRQMBCooaBwUAQYAJCy0AQQBC/rnrxemOlZkQNwKQiQFBAEKBxpS6lvHq5m83AoiJAUEAQgA3AoCJAQu+BQEHf0EAQQAoAoCJASIBIABqQf////8BcSICNgKAiQFBAEEAKAKEiQEgAiABSWogAEEddmo2AoSJAQJAAkACQAJAAkACQCABQT9xIgMNAEGACSEEDAELQcAAIANrIgUgAEsNASAFQQNxIQZBACEBAkAgA0E/c0EDSQ0AIANBgIkBaiEEIAVB/ABxIQdBACEBA0AgBCABaiICQRhqIAFBgAlqLQAAOgAAIAJBGWogAUGBCWotAAA6AAAgAkEaaiABQYIJai0AADoAACACQRtqIAFBgwlqLQAAOgAAIAcgAUEEaiIBRw0ACwsCQCAGRQ0AIANBmIkBaiECA0AgAiABaiABQYAJai0AADoAACABQQFqIQEgBkF/aiIGDQALC0GYiQFBwAAQAxogACAFayEAIAVBgAlqIQQLIABBwABPDQEgACECDAILIABFDQIgAEEDcSEGQQAhAQJAIABBBEkNACADQYCJAWohBCAAQXxxIQBBACEBA0AgBCABaiICQRhqIAFBgAlqLQAAOgAAIAJBGWogAUGBCWotAAA6AAAgAkEaaiABQYIJai0AADoAACACQRtqIAFBgwlqLQAAOgAAIAAgAUEEaiIBRw0ACwsgBkUNAiADQZiJAWohAgNAIAIgAWogAUGACWotAAA6AAAgAUEBaiEBIAZBf2oiBg0ADAMLCyAAQT9xIQIgBCAAQUBxEAMhBAsgAkUNACACQQNxIQZBACEBAkAgAkEESQ0AIAJBPHEhAEEAIQEDQCABQZiJAWogBCABaiICLQAAOgAAIAFBmYkBaiACQQFqLQAAOgAAIAFBmokBaiACQQJqLQAAOgAAIAFBm4kBaiACQQNqLQAAOgAAIAAgAUEEaiIBRw0ACwsgBkUNAANAIAFBmIkBaiAEIAFqLQAAOgAAIAFBAWohASAGQX9qIgYNAAsLC4cQARl/QQAoApSJASECQQAoApCJASEDQQAoAoyJASEEQQAoAoiJASEFA0AgACgCCCIGIAAoAhgiByAAKAIoIgggACgCOCIJIAAoAjwiCiAAKAIMIgsgACgCHCIMIAAoAiwiDSAMIAsgCiANIAkgCCAHIAMgBmogAiAAKAIEIg5qIAUgBCACIANzcSACc2ogACgCACIPakH4yKq7fWpBB3cgBGoiECAEIANzcSADc2pB1u6exn5qQQx3IBBqIhEgECAEc3EgBHNqQdvhgaECakERdyARaiISaiAAKAIUIhMgEWogACgCECIUIBBqIAQgC2ogEiARIBBzcSAQc2pB7p33jXxqQRZ3IBJqIhAgEiARc3EgEXNqQa+f8Kt/akEHdyAQaiIRIBAgEnNxIBJzakGqjJ+8BGpBDHcgEWoiEiARIBBzcSAQc2pBk4zBwXpqQRF3IBJqIhVqIAAoAiQiFiASaiAAKAIgIhcgEWogDCAQaiAVIBIgEXNxIBFzakGBqppqakEWdyAVaiIQIBUgEnNxIBJzakHYsYLMBmpBB3cgEGoiESAQIBVzcSAVc2pBr++T2nhqQQx3IBFqIhIgESAQc3EgEHNqQbG3fWpBEXcgEmoiFWogACgCNCIYIBJqIAAoAjAiGSARaiANIBBqIBUgEiARc3EgEXNqQb6v88p4akEWdyAVaiIQIBUgEnNxIBJzakGiosDcBmpBB3cgEGoiESAQIBVzcSAVc2pBk+PhbGpBDHcgEWoiFSARIBBzcSAQc2pBjofls3pqQRF3IBVqIhJqIAcgFWogDiARaiAKIBBqIBIgFSARc3EgEXNqQaGQ0M0EakEWdyASaiIQIBJzIBVxIBJzakHiyviwf2pBBXcgEGoiESAQcyAScSAQc2pBwOaCgnxqQQl3IBFqIhIgEXMgEHEgEXNqQdG0+bICakEOdyASaiIVaiAIIBJqIBMgEWogDyAQaiAVIBJzIBFxIBJzakGqj9vNfmpBFHcgFWoiECAVcyAScSAVc2pB3aC8sX1qQQV3IBBqIhEgEHMgFXEgEHNqQdOokBJqQQl3IBFqIhIgEXMgEHEgEXNqQYHNh8V9akEOdyASaiIVaiAJIBJqIBYgEWogFCAQaiAVIBJzIBFxIBJzakHI98++fmpBFHcgFWoiECAVcyAScSAVc2pB5puHjwJqQQV3IBBqIhEgEHMgFXEgEHNqQdaP3Jl8akEJdyARaiISIBFzIBBxIBFzakGHm9Smf2pBDncgEmoiFWogBiASaiAYIBFqIBcgEGogFSAScyARcSASc2pB7anoqgRqQRR3IBVqIhAgFXMgEnEgFXNqQYXSj896akEFdyAQaiIRIBBzIBVxIBBzakH4x75nakEJdyARaiISIBFzIBBxIBFzakHZhby7BmpBDncgEmoiFWogFyASaiATIBFqIBkgEGogFSAScyARcSASc2pBipmp6XhqQRR3IBVqIhAgFXMiFSASc2pBwvJoakEEdyAQaiIRIBVzakGB7ce7eGpBC3cgEWoiEiARcyIaIBBzakGiwvXsBmpBEHcgEmoiFWogFCASaiAOIBFqIAkgEGogFSAac2pBjPCUb2pBF3cgFWoiECAVcyIVIBJzakHE1PulempBBHcgEGoiESAVc2pBqZ/73gRqQQt3IBFqIhIgEXMiCSAQc2pB4JbttX9qQRB3IBJqIhVqIA8gEmogGCARaiAIIBBqIBUgCXNqQfD4/vV7akEXdyAVaiIQIBVzIhUgEnNqQcb97cQCakEEdyAQaiIRIBVzakH6z4TVfmpBC3cgEWoiEiARcyIIIBBzakGF4bynfWpBEHcgEmoiFWogGSASaiAWIBFqIAcgEGogFSAIc2pBhbqgJGpBF3cgFWoiESAVcyIQIBJzakG5oNPOfWpBBHcgEWoiEiAQc2pB5bPutn5qQQt3IBJqIhUgEnMiByARc2pB+PmJ/QFqQRB3IBVqIhBqIAwgFWogDyASaiAGIBFqIBAgB3NqQeWssaV8akEXdyAQaiIRIBVBf3NyIBBzakHExKShf2pBBncgEWoiEiAQQX9zciARc2pBl/+rmQRqQQp3IBJqIhAgEUF/c3IgEnNqQafH0Nx6akEPdyAQaiIVaiALIBBqIBkgEmogEyARaiAVIBJBf3NyIBBzakG5wM5kakEVdyAVaiIRIBBBf3NyIBVzakHDs+2qBmpBBncgEWoiECAVQX9zciARc2pBkpmz+HhqQQp3IBBqIhIgEUF/c3IgEHNqQf3ov39qQQ93IBJqIhVqIAogEmogFyAQaiAOIBFqIBUgEEF/c3IgEnNqQdG7kax4akEVdyAVaiIQIBJBf3NyIBVzakHP/KH9BmpBBncgEGoiESAVQX9zciAQc2pB4M2zcWpBCncgEWoiEiAQQX9zciARc2pBlIaFmHpqQQ93IBJqIhVqIA0gEmogFCARaiAYIBBqIBUgEUF/c3IgEnNqQaGjoPAEakEVdyAVaiIQIBJBf3NyIBVzakGC/c26f2pBBncgEGoiESAVQX9zciAQc2pBteTr6XtqQQp3IBFqIhIgEEF/c3IgEXNqQbul39YCakEPdyASaiIVIARqIBYgEGogFSARQX9zciASc2pBkaeb3H5qQRV3aiEEIBUgA2ohAyASIAJqIQIgESAFaiEFIABBwABqIQAgAUFAaiIBDQALQQAgAjYClIkBQQAgAzYCkIkBQQAgBDYCjIkBQQAgBTYCiIkBIAALzwMBBH9BACgCgIkBQT9xIgBBmIkBakGAAToAACAAQQFqIQECQAJAAkACQCAAQT9zIgJBB0sNACACRQ0BIAFBmIkBakEAOgAAIAJBAUYNASAAQZqJAWpBADoAACACQQJGDQEgAEGbiQFqQQA6AAAgAkEDRg0BIABBnIkBakEAOgAAIAJBBEYNASAAQZ2JAWpBADoAACACQQVGDQEgAEGeiQFqQQA6AAAgAkEGRg0BIABBn4kBakEAOgAADAELIAJBCEYNAkE2IABrIQMCQCACQQNxIgANACADIQIMAgtBACAAayECQQAhAANAIABBz4kBakEAOgAAIAIgAEF/aiIARw0ACyADIABqIQIMAQtBmIkBQcAAEAMaQQAhAUE3IQNBNyECCyADQQNJDQAgAUGAiQFqIQBBfyEBA0AgACACakEVakEANgAAIABBfGohACACIAFBBGoiAUcNAAsLQQBBACgChIkBNgLUiQFBAEEAKAKAiQEiAEEVdjoA04kBQQAgAEENdjoA0okBQQAgAEEFdjoA0YkBQQAgAEEDdCIAOgDQiQFBACAANgKAiQFBmIkBQcAAEAMaQQBBACkCiIkBNwOACUEAQQApApCJATcDiAkLBgBBgIkBCzMAQQBC/rnrxemOlZkQNwKQiQFBAEKBxpS6lvHq5m83AoiJAUEAQgA3AoCJASAAEAIQBAsLCwEAQYAICwSYAAAA";
var hash$d = "42fa4d29";
var wasmJson$d = {
  name: name$d,
  data: data$d,
  hash: hash$d
};
var mutex$e = new Mutex();
var wasmCache$e = null;
function md5(data) {
  if (wasmCache$e === null) {
    return lockedCreate(mutex$e, wasmJson$d, 16).then((wasm) => {
      wasmCache$e = wasm;
      return wasmCache$e.calculate(data);
    });
  }
  try {
    const hash = wasmCache$e.calculate(data);
    return Promise.resolve(hash);
  } catch (err) {
    return Promise.reject(err);
  }
}
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();
new Mutex();

// ../base/upload/md5.ts
function blobToMd5(chunk) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      if (!(e.target?.result && e.target.result instanceof ArrayBuffer)) {
        return reject(new Error("Could not read file"));
      }
      const view = new Uint8Array(e.target.result);
      const f = await md5(view);
      resolve(f);
    };
    fileReader.readAsArrayBuffer(chunk);
  });
}

// ../base/upload/methods/multipart.ts
var MAX_PARTS = 1e3;
var MAX_PARTS_PAGE_SIZE = 50;
function calculateParts(file, desiredPartSize) {
  const partCount = Math.min(Math.ceil(file.size / desiredPartSize), MAX_PARTS);
  const partSize = Math.ceil(file.size / partCount);
  return [partSize, partCount];
}
var UploadMultiPart = class extends UploadFile {
  canPause = true;
  // Initialize default part options:
  multiPartOptions;
  partSize = -1;
  partCount = -1;
  parts = [];
  partsProgress = {};
  partsPromise;
  page = 1;
  pageLength;
  pageCount = 1;
  getParts;
  completeUpload;
  constructor(params, opts) {
    super(params);
    this.getParts = params.getParts;
    this.completeUpload = params.completeUpload;
    this.multiPartOptions = opts.multiPartOptions;
    const [partSize, partCount] = calculateParts(this.file, this.multiPartOptions.partSize);
    this.partSize = partSize;
    this.partCount = partCount;
    this.page = 1;
    this.pageLength = Math.min(this.partCount, MAX_PARTS_PAGE_SIZE);
    this.pageCount = Math.ceil(this.partCount / this.pageLength);
  }
  _fetchParts() {
    if (this.partsPromise) {
      this._log(`Already fetching parts`);
      return this.partsPromise;
    }
    if (this.parts.length > this.pageLength) {
      this._log(`Parts length (${this.parts.length}) exceeds page length (${this.pageLength})`);
      return;
    }
    if (this.page > this.pageCount) {
      this._log(`No more parts to fetch, page (${this.page}) exceeds page count (${this.pageCount})`);
      return;
    }
    this._log("Fetching more parts", {
      page: this.page,
      pageCount: this.pageCount,
      pageLength: this.pageLength,
      partsLength: this.parts.length
    });
    const asset = this.getState().asset;
    if (!asset) {
      throw new Error("Missing upload asset!");
    }
    this.partsPromise = this.getParts(
      {
        page: this.page,
        pageLength: this.pageLength,
        asset
      },
      {
        signal: this.abortController.signal,
        debug: this.debug
      }
    ).then((parts) => {
      const remainder = this.page * this.pageLength - this.partCount;
      if (remainder > 0) {
        this._log(`Removing extraneous parts (${remainder}) from array`);
        parts = parts.slice(0, this.pageLength - remainder);
      }
      this._log(`Pushing more parts (${parts.length}) onto parts array (${this.parts.length})`);
      this.parts.push(...parts);
      this.page++;
      this._log("Clearing parts promise");
      this.partsPromise = void 0;
    });
    return this.partsPromise;
  }
  async _getNextPart() {
    this._fetchParts();
    if (this.parts.length) {
      this._log(`Returning part (length: ${this.parts.length})`);
      return this.parts.shift();
    }
    if (this.partsPromise) {
      this._log("Waiting for more parts");
      await this.partsPromise;
      return this.parts.shift();
    }
  }
  _updateProgress(partId, partProgress) {
    this.partsProgress[partId] = partProgress;
    const allPartsProgress = Object.values(this.partsProgress).reduce((acc, progress) => {
      acc += progress;
      return acc;
    }, 0);
    this.setState((s) => {
      s.progress = allPartsProgress / (this.partCount + 1);
    });
  }
  _startNextPart() {
    if (this.state.state === "paused") {
      return;
    }
    if (this.state.state === "finished" || this.state.state === "aborted" || this.state.state === "failed") {
      return;
    }
    if (this.state.progress >= 1) {
      return;
    }
    return this._getNextPart().then((nextPart) => {
      if (!nextPart) {
        this._log("No next part returned");
        return;
      }
      if ("etag" in nextPart && nextPart.etag) {
        this._log("Part is already finished", nextPart);
        this._updateProgress(nextPart.id, 1);
        return this._startNextPart();
      }
      const startBytes = (nextPart.id - 1) * this.partSize;
      const endBytes = startBytes + this.partSize;
      const data = this.file.slice(startBytes, endBytes);
      return this._putPart(nextPart, data, 0).then(() => {
        this._log("Part finished, next!");
        return this._startNextPart();
      });
    });
  }
  async _putPart(part, data, attempt) {
    try {
      const [res, md52] = await Promise.all([
        // Put chunk
        httpPut(part.upload_link, data, {
          signal: this.abortController.signal,
          onProgress: (progress) => {
            this._updateProgress(
              part.id,
              // Once the file has been uploaded, it still takes a while for the HTTP
              // request to finish. That's why we're only counting the file upload
              // progress as 90% of the total. The other 10% is for the request to complete.
              progress * 0.9
            );
          }
        }).then((res2) => {
          this._updateProgress(part.id, 1);
          return res2;
        }),
        // Hash chunk
        blobToMd5(data).then((res2) => {
          return res2;
        })
      ]);
      const etag = (res.headers["etag"] ?? "").replaceAll('"', "");
      if (md52 !== etag) {
        throw new Error("Chunk hashes do not match");
      }
    } catch (err) {
      this._log(err);
      if (this.state.state === "aborted" || this.abortController.signal.aborted) {
        this.setState((s) => {
          s.state = "aborted";
        });
        return;
      }
      this.setState((s) => {
        s.errors = [...s.errors, err];
      });
      if (attempt >= this.multiPartOptions.maxRetries) {
        this._log("Maximum part retry attempts achieved, failing upload...");
        this.abortController.abort();
        this.setState((s) => {
          s.errors = [...s.errors, err];
          s.state = "failed";
        });
        throw err;
      }
      this._log(`Retrying part after time-out (${this.multiPartOptions.timeBetweenRetries})`, part);
      await new Promise((resolve) => {
        setTimeout(resolve, this.multiPartOptions.timeBetweenRetries);
      });
      return this._putPart(part, data, attempt + 1);
    }
  }
  async _upload() {
    let currentState = this.state.state;
    if (currentState === "finished" || currentState === "failed" || currentState === "aborted" || currentState === "paused") {
      return;
    }
    const promises = [];
    const partsInProgress = Object.values(this.partsProgress).filter((p) => p < 1).length;
    const partsToStart = this.multiPartOptions.maxConcurrentParts - partsInProgress;
    this._log("Starting uploading", partsToStart, "parts");
    for (let i = 0; i < partsToStart; i++) {
      promises.push(this._startNextPart());
      await new Promise((r) => setTimeout(r, 50));
    }
    await Promise.all(promises);
    currentState = this.state.state;
    if (currentState === "aborted" || currentState === "failed") {
      return;
    }
    const allProgress = Object.values(this.partsProgress);
    if (allProgress.length === this.partCount && allProgress.every((p) => p === 1)) {
      await this.handleUploadFinished();
    }
  }
  async handleUploadFinished() {
    const asset = this.getState().asset;
    if (!asset) {
      throw new Error("Missing upload asset!");
    }
    await this.completeUpload(
      {
        asset
      },
      {
        signal: this.abortController.signal,
        debug: this.debug
      }
    );
    await super.handleUploadFinished();
  }
};

// ../../node_modules/lodash-es/_freeGlobal.js
var freeGlobal = typeof global == "object" && global && global.Object === Object && global;
var freeGlobal_default = freeGlobal;

// ../../node_modules/lodash-es/_root.js
var freeSelf = typeof self == "object" && self && self.Object === Object && self;
var root = freeGlobal_default || freeSelf || Function("return this")();
var root_default = root;

// ../../node_modules/lodash-es/_Symbol.js
var Symbol = root_default.Symbol;
var Symbol_default = Symbol;

// ../../node_modules/lodash-es/_getRawTag.js
var objectProto = Object.prototype;
var hasOwnProperty = objectProto.hasOwnProperty;
var nativeObjectToString = objectProto.toString;
var symToStringTag = Symbol_default ? Symbol_default.toStringTag : void 0;
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag), tag = value[symToStringTag];
  try {
    value[symToStringTag] = void 0;
    var unmasked = true;
  } catch (e) {
  }
  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}
var getRawTag_default = getRawTag;

// ../../node_modules/lodash-es/_objectToString.js
var objectProto2 = Object.prototype;
var nativeObjectToString2 = objectProto2.toString;
function objectToString(value) {
  return nativeObjectToString2.call(value);
}
var objectToString_default = objectToString;

// ../../node_modules/lodash-es/_baseGetTag.js
var nullTag = "[object Null]";
var undefinedTag = "[object Undefined]";
var symToStringTag2 = Symbol_default ? Symbol_default.toStringTag : void 0;
function baseGetTag(value) {
  if (value == null) {
    return value === void 0 ? undefinedTag : nullTag;
  }
  return symToStringTag2 && symToStringTag2 in Object(value) ? getRawTag_default(value) : objectToString_default(value);
}
var baseGetTag_default = baseGetTag;

// ../../node_modules/lodash-es/isObjectLike.js
function isObjectLike(value) {
  return value != null && typeof value == "object";
}
var isObjectLike_default = isObjectLike;

// ../../node_modules/lodash-es/isSymbol.js
var symbolTag = "[object Symbol]";
function isSymbol(value) {
  return typeof value == "symbol" || isObjectLike_default(value) && baseGetTag_default(value) == symbolTag;
}
var isSymbol_default = isSymbol;

// ../../node_modules/lodash-es/_arrayMap.js
function arrayMap(array, iteratee) {
  var index = -1, length = array == null ? 0 : array.length, result = Array(length);
  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}
var arrayMap_default = arrayMap;

// ../../node_modules/lodash-es/isArray.js
var isArray = Array.isArray;
var isArray_default = isArray;

// ../../node_modules/lodash-es/_baseToString.js
var INFINITY = 1 / 0;
var symbolProto = Symbol_default ? Symbol_default.prototype : void 0;
var symbolToString = symbolProto ? symbolProto.toString : void 0;
function baseToString(value) {
  if (typeof value == "string") {
    return value;
  }
  if (isArray_default(value)) {
    return arrayMap_default(value, baseToString) + "";
  }
  if (isSymbol_default(value)) {
    return symbolToString ? symbolToString.call(value) : "";
  }
  var result = value + "";
  return result == "0" && 1 / value == -INFINITY ? "-0" : result;
}
var baseToString_default = baseToString;

// ../../node_modules/lodash-es/toString.js
function toString(value) {
  return value == null ? "" : baseToString_default(value);
}
var toString_default = toString;

// ../../node_modules/lodash-es/_baseSlice.js
function baseSlice(array, start, end) {
  var index = -1, length = array.length;
  if (start < 0) {
    start = -start > length ? 0 : length + start;
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : end - start >>> 0;
  start >>>= 0;
  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}
var baseSlice_default = baseSlice;

// ../../node_modules/lodash-es/_castSlice.js
function castSlice(array, start, end) {
  var length = array.length;
  end = end === void 0 ? length : end;
  return !start && end >= length ? array : baseSlice_default(array, start, end);
}
var castSlice_default = castSlice;

// ../../node_modules/lodash-es/_hasUnicode.js
var rsAstralRange = "\\ud800-\\udfff";
var rsComboMarksRange = "\\u0300-\\u036f";
var reComboHalfMarksRange = "\\ufe20-\\ufe2f";
var rsComboSymbolsRange = "\\u20d0-\\u20ff";
var rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange;
var rsVarRange = "\\ufe0e\\ufe0f";
var rsZWJ = "\\u200d";
var reHasUnicode = RegExp("[" + rsZWJ + rsAstralRange + rsComboRange + rsVarRange + "]");
function hasUnicode(string) {
  return reHasUnicode.test(string);
}
var hasUnicode_default = hasUnicode;

// ../../node_modules/lodash-es/_asciiToArray.js
function asciiToArray(string) {
  return string.split("");
}
var asciiToArray_default = asciiToArray;

// ../../node_modules/lodash-es/_unicodeToArray.js
var rsAstralRange2 = "\\ud800-\\udfff";
var rsComboMarksRange2 = "\\u0300-\\u036f";
var reComboHalfMarksRange2 = "\\ufe20-\\ufe2f";
var rsComboSymbolsRange2 = "\\u20d0-\\u20ff";
var rsComboRange2 = rsComboMarksRange2 + reComboHalfMarksRange2 + rsComboSymbolsRange2;
var rsVarRange2 = "\\ufe0e\\ufe0f";
var rsAstral = "[" + rsAstralRange2 + "]";
var rsCombo = "[" + rsComboRange2 + "]";
var rsFitz = "\\ud83c[\\udffb-\\udfff]";
var rsModifier = "(?:" + rsCombo + "|" + rsFitz + ")";
var rsNonAstral = "[^" + rsAstralRange2 + "]";
var rsRegional = "(?:\\ud83c[\\udde6-\\uddff]){2}";
var rsSurrPair = "[\\ud800-\\udbff][\\udc00-\\udfff]";
var rsZWJ2 = "\\u200d";
var reOptMod = rsModifier + "?";
var rsOptVar = "[" + rsVarRange2 + "]?";
var rsOptJoin = "(?:" + rsZWJ2 + "(?:" + [rsNonAstral, rsRegional, rsSurrPair].join("|") + ")" + rsOptVar + reOptMod + ")*";
var rsSeq = rsOptVar + reOptMod + rsOptJoin;
var rsSymbol = "(?:" + [rsNonAstral + rsCombo + "?", rsCombo, rsRegional, rsSurrPair, rsAstral].join("|") + ")";
var reUnicode = RegExp(rsFitz + "(?=" + rsFitz + ")|" + rsSymbol + rsSeq, "g");
function unicodeToArray(string) {
  return string.match(reUnicode) || [];
}
var unicodeToArray_default = unicodeToArray;

// ../../node_modules/lodash-es/_stringToArray.js
function stringToArray(string) {
  return hasUnicode_default(string) ? unicodeToArray_default(string) : asciiToArray_default(string);
}
var stringToArray_default = stringToArray;

// ../../node_modules/lodash-es/_createCaseFirst.js
function createCaseFirst(methodName) {
  return function(string) {
    string = toString_default(string);
    var strSymbols = hasUnicode_default(string) ? stringToArray_default(string) : void 0;
    var chr = strSymbols ? strSymbols[0] : string.charAt(0);
    var trailing = strSymbols ? castSlice_default(strSymbols, 1).join("") : string.slice(1);
    return chr[methodName]() + trailing;
  };
}
var createCaseFirst_default = createCaseFirst;

// ../../node_modules/lodash-es/upperFirst.js
var upperFirst = createCaseFirst_default("toUpperCase");
var upperFirst_default = upperFirst;

// ../base/upload/util.ts
function cleanFilename(name) {
  if (typeof name === "undefined") {
    return void 0;
  }
  const parts = name.split(".");
  if (parts.length >= 2) {
    parts.pop();
  }
  const filename = parts.join(".");
  return filename.replace(/(_|-{2,})/g, " ").replace(/\w\S*/g, (txt) => {
    return upperFirst_default(txt);
  });
}

// src/upload/index.ts
async function jwUpload(type, params, opts) {
  const conf = (await (await import('./conf-IVD3ECB4.js')).default[type]())["default"];
  const metadata = {
    cleanedTitle: "Unknown"
  };
  if ("file" in params.upload) {
    metadata.cleanedTitle = cleanFilename(params.upload.file.name);
  } else if ("externalUrl" in params.upload) {
    metadata.cleanedTitle = cleanFilename(params.upload.externalUrl.split("/").pop());
  } else if ("fetchUrl" in params.upload) {
    metadata.cleanedTitle = cleanFilename(params.upload.fetchUrl.split("/").pop());
  }
  const paramsConverted = conf.convertParams(params, metadata);
  if (!paramsConverted) {
    throw new Error("Could not convert params!");
  }
  const createAsset = async () => {
    const [group, action] = conf.createOperation;
    return (await jwApi(
      group,
      action,
      paramsConverted,
      // this is a dynamic function, so TS does not like this
      opts
    )).data;
  };
  const getIdentifier = (id) => {
    return {
      id,
      routeParams: params.routeParams
    };
  };
  switch (paramsConverted.input.upload.method) {
    case "direct": {
      if (!("file" in params.upload)) {
        throw new Error("Missing File to upload!");
      }
      const file = params.upload.file;
      return new UploadDirect({
        metadata,
        file,
        createAsset,
        getIdentifier,
        debug: opts?.debug,
        mimeType: file.type
      });
    }
    case "multipart": {
      if (!("file" in params.upload)) {
        throw new Error("Missing File to upload!");
      }
      const file = params.upload.file;
      return new UploadMultiPart(
        {
          metadata,
          file,
          createAsset,
          getIdentifier,
          debug: opts?.debug,
          getParts: async (params2, opts2) => {
            const uploadId = params2.asset.upload_id;
            if (!uploadId) {
              throw new Error("Asset does not have upload_id!");
            }
            const uploadToken = params2.asset.upload_token;
            if (!uploadToken) {
              throw new Error("Asset does not have upload_token!");
            }
            return (await jwApi(
              "uploads",
              "getParts",
              {
                routeParams: {
                  uploadId
                },
                headers: {
                  Authorization: `Bearer ${uploadToken}`
                },
                queryParams: {
                  page: params2.page,
                  page_length: params2.pageLength
                }
              },
              opts2
            )).data.parts;
          },
          completeUpload: async (params2, opts2) => {
            const uploadId = params2.asset.upload_id;
            if (!uploadId) {
              throw new Error("Asset does not have upload_id!");
            }
            const uploadToken = params2.asset.upload_token;
            if (!uploadToken) {
              throw new Error("Asset does not have upload_token!");
            }
            await jwApi(
              "uploads",
              "complete",
              {
                routeParams: {
                  uploadId
                },
                headers: {
                  Authorization: `Bearer ${uploadToken}`
                }
              },
              opts2
            );
          }
        },
        {
          multiPartOptions: config.get().upload.multipart
        }
      );
    }
    case "external": {
      if (!("externalUrl" in params.upload)) {
        throw new Error("Undefined params.upload.externalUrl");
      }
      return new UploadExternal({
        metadata,
        url: params.upload.externalUrl,
        createAsset,
        getIdentifier,
        debug: opts?.debug
      });
    }
    case "fetch": {
      if (!("fetchUrl" in params.upload)) {
        throw new Error("Undefined params.upload.fetchUrl");
      }
      return new UploadFetch({
        metadata,
        url: params.upload.fetchUrl,
        createAsset,
        getIdentifier,
        debug: opts?.debug
      });
    }
  }
}
/*! Bundled license information:

hash-wasm/dist/index.esm.js:
  (*!
   * hash-wasm (https://www.npmjs.com/package/hash-wasm)
   * (c) Dani Biro
   * @license MIT
   *)

lodash-es/lodash.js:
  (**
   * @license
   * Lodash (Custom Build) <https://lodash.com/>
   * Build: `lodash modularize exports="es" -o ./`
   * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
   * Released under MIT license <https://lodash.com/license>
   * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
   * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
   *)
*/

export { Upload, UploadManager, applyConfig, jwUpload };
